import React from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Pair, Token, Currency } from 'vvs-sdk'
import { Text, Flex, Heading, ImpermanentLossIcon, useTooltip, Box, HelpIcon, Link } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { formatNumber } from 'utils/formatBalance'
import getFiatSymbol from 'utils/getFiatSymbol'
import getFiatValue from 'utils/getFiatValue'
import useTheme from 'hooks/useTheme'
import { usePriceVvsUsdc } from 'state/farms/hooks'

export interface ImpermanentLossProps {
  token: Token
  tokenPriceUsdc: string
  quoteTokenPriceUsdc: string
  pair: Pair
  pendingVVS: BigNumber
  token0Supplied: BigNumber
  token1Supplied: BigNumber
  avgToken0Price: string
  avgToken1Price: string
  totalHarvestedVVS: BigNumber
  stakedLPAmount: BigNumber
  lpTokenPrice: BigNumber
}

const Wrapper = styled.div`
  margin-top: 24px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.95fr 0.15fr;
  margin-top: 16px;
  margin-bottom: 16px;
`
const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr;
`

const ImpermanentLoss: React.FC<ImpermanentLossProps> = ({
  token,
  tokenPriceUsdc,
  quoteTokenPriceUsdc,
  pair,
  pendingVVS,
  token0Supplied,
  token1Supplied,
  avgToken0Price,
  avgToken1Price,
  totalHarvestedVVS,
  stakedLPAmount,
  lpTokenPrice,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const symbol: string = getFiatSymbol()

  // unwrapped pair tokens
  const token0: Currency = unwrappedToken(pair.token0)
  const token1: Currency = unwrappedToken(pair.token1)

  // current price
  let token0CurrentPrice: BigNumber
  let token1CurrentPrice: BigNumber
  if (token.symbol === pair.token0.symbol) {
    token0CurrentPrice = new BigNumber(tokenPriceUsdc)
    token1CurrentPrice = new BigNumber(quoteTokenPriceUsdc)
  } else {
    token0CurrentPrice = new BigNumber(quoteTokenPriceUsdc)
    token1CurrentPrice = new BigNumber(tokenPriceUsdc)
  }
  const token0CurrentPriceDisplay: string = token0CurrentPrice.gte(1)
    ? formatNumber(getFiatValue(token0CurrentPrice).toNumber())
    : getFiatValue(token0CurrentPrice).toPrecision(2)
  const token1CurrentPriceDisplay: string = token1CurrentPrice.gte(1)
    ? formatNumber(getFiatValue(token1CurrentPrice).toNumber())
    : getFiatValue(token1CurrentPrice).toPrecision(2)

  // avg token price
  const avgToken0PriceBN: BigNumber = new BigNumber(avgToken0Price)
  const avgToken1PriceBN: BigNumber = new BigNumber(avgToken1Price)
  const avgToken0PriceDisplay: string = avgToken0PriceBN.gte(1)
    ? formatNumber(getFiatValue(avgToken0PriceBN).toNumber())
    : getFiatValue(avgToken0PriceBN).toPrecision(2)
  const avgToken1PriceDisplay: string = avgToken1PriceBN.gte(1)
    ? formatNumber(getFiatValue(avgToken1PriceBN).toNumber())
    : getFiatValue(avgToken1PriceBN).toPrecision(2)

  // price change
  const token0PriceChange: BigNumber = new BigNumber(token0CurrentPrice).div(avgToken0Price).minus(1).times(100)
  const token1PriceChange: BigNumber = new BigNumber(token1CurrentPrice).div(avgToken1Price).minus(1).times(100)

  // hodl value
  const hodlValue: BigNumber = getFiatValue(
    token0Supplied.times(token0CurrentPrice).plus(token1Supplied.times(token1CurrentPrice)),
  )

  // current removal value
  const removalValue: BigNumber = getFiatValue(lpTokenPrice.times(stakedLPAmount))

  // impermanent loss
  const impermanentLoss: BigNumber = removalValue.minus(hodlValue)
  const impermanentLossPercent: BigNumber = impermanentLoss.div(hodlValue).times(100)

  // tooltip
  const TooltipComponent = () => (
    <>
      <Text color={theme.colors.white}>
        {t(
          'Impermanent Loss is an unrealized loss. Numbers are estimates and for reference only. Please always do your own research and calculation!',
        )}
      </Text>
      <Link href="https://docs.vvs.finance/product-guides/crystal-farms/pool-insights" target="_blank">
        {t('Learn More')}
      </Link>
    </>
  )
  const { targetRef, tooltip, tooltipVisible } = useTooltip(<TooltipComponent />, {
    placement: 'bottom-start',
  })

  // vvs price
  const vvsPrice = usePriceVvsUsdc()
  const totalHarvestedVVSPrice: string = getFiatValue(totalHarvestedVVS.times(vvsPrice)).toFixed(2)
  const pendingVVSPrice: string = getFiatValue(pendingVVS.times(vvsPrice)).toFixed(2)

  return (
    <Wrapper>
      {tooltipVisible && tooltip}
      <Flex mt="20px" mb="20px">
        <ImpermanentLossIcon mr="8px" />
        <Heading>{t('Impermanent Loss Information (BETA)')}</Heading>
        <Box ref={targetRef} mt="2px" ml="15px">
          <HelpIcon color="primary" />
        </Box>
      </Flex>

      <Row>
        <Flex alignSelf="center">
          <Text color="textSubtle" fontSize="13px">
            {t('Current price')}
          </Text>
        </Flex>
        <Flex flexDirection="column">
          <PriceGrid>
            <Text>{token0.symbol}</Text>
            <Text>{`${symbol}${token0CurrentPriceDisplay}`}</Text>
          </PriceGrid>
          <PriceGrid>
            <Text>{token1.symbol}</Text>
            <Text>{`${symbol}${token1CurrentPriceDisplay}`}</Text>
          </PriceGrid>
        </Flex>
        <Flex />
      </Row>
      <Row>
        <Flex alignSelf="center">
          <Text color="textSubtle" fontSize="13px">
            {t('Avg Deposit Price')}
          </Text>
        </Flex>
        <Flex flexDirection="column">
          <PriceGrid>
            <Text>{token0.symbol}</Text>
            <Text>{`${symbol}${avgToken0PriceDisplay}`}</Text>
          </PriceGrid>
          <PriceGrid>
            <Text>{token1.symbol}</Text>
            <Text>{`${symbol}${avgToken1PriceDisplay}`}</Text>
          </PriceGrid>
        </Flex>
        <Flex />
      </Row>
      <Row>
        <Flex alignSelf="center">
          <Text color="textSubtle" fontSize="13px">
            {t('Price Change')}
          </Text>
        </Flex>
        <Flex flexDirection="column">
          <PriceGrid>
            <Text>{token0.symbol}</Text>
            <Text
              color={token0PriceChange.gt(0) ? theme.colors.success : theme.colors.failure}
            >{`(${token0PriceChange.toFixed(2)}%)`}</Text>
          </PriceGrid>
          <PriceGrid>
            <Text>{token1.symbol}</Text>
            <Text
              color={token1PriceChange.gt(0) ? theme.colors.success : theme.colors.failure}
            >{`(${token1PriceChange.toFixed(2)}%)`}</Text>
          </PriceGrid>
        </Flex>
        <Flex />
      </Row>

      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('HODL Value')}
          </Text>
        </Flex>
        <Text>{`${symbol}${formatNumber(hodlValue.toNumber())}`}</Text>
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Current Removal Value')}
          </Text>
        </Flex>
        <Text>{`${symbol}${formatNumber(removalValue.toNumber())}`}</Text>
        <Flex />
      </Row>
      <Row>
        <Flex flexDirection="column">
          <Text color="textSubtle" fontSize="13px">
            {t('Impermanent Loss')}
          </Text>
          <Text color="textSubtle" fontSize="13px">
            {t('(Unrealized)')}
          </Text>
        </Flex>
        <Flex alignSelf="center">
          <Text color={impermanentLoss.gt(0) ? theme.colors.success : theme.colors.failure}>
            {impermanentLoss.gt(0)
              ? `${symbol}${formatNumber(impermanentLoss.toNumber())} (${impermanentLossPercent.toFixed(2)}%)`
              : `-${symbol}${formatNumber(
                  impermanentLoss.times(impermanentLoss.s).toNumber(),
                )} (${impermanentLossPercent.toFixed(2)}%)`}
          </Text>
        </Flex>
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('LP Earnings')}
          </Text>
        </Flex>
        <Text>Coming Next!</Text>
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Total Harvested VVS')}
          </Text>
        </Flex>
        <Text color={theme.colors.success}>{`${formatNumber(
          totalHarvestedVVS.toNumber(),
          6,
          6,
        )} (${symbol}${totalHarvestedVVSPrice})`}</Text>
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Pending VVS')}
          </Text>
        </Flex>
        <Text color={theme.colors.success}>{`${formatNumber(
          pendingVVS.toNumber(),
          6,
          6,
        )} (${symbol}${pendingVVSPrice})`}</Text>
      </Row>
      {/* <RoiRow>
        <Flex mt="10px">
          <Text color="textSubtle" bold fontSize="20px">
            {t('Total ROI')}
          </Text>
        </Flex>
        <Flex mt="10px">
          <Text bold fontSize="20px">
            {t('Total ROI')}
          </Text>
        </Flex>
        <Flex />
        </RoiRow> */}
    </Wrapper>
  )
}

export default ImpermanentLoss
